import React from "react";

import {Dropdown} from "primereact/dropdown";
import {SelectButton} from "primereact/selectbutton";
import {InputText} from "primereact/inputtext";

import Number from "../Common/Number";
import {Tooltip} from "../../utils/InlineComponents";
import {getKeyValue} from "../../utils/Utils";
import {protectionStrategies} from "../../utils/Constants";
import PricingModeGuide from "./PricingModeGuide";

// Controlled, presentational form for editing the RULE fields of a pricing
// template. It reuses the exact PrimeReact controls, option arrays and i18n
// keys used by ProductReprice so the template form behaves identically to the
// per-product form for these fields. Per-product only fields (min_value,
// optimum_value, scope) are intentionally excluded.
//
// Repricing rules are shared across ALL of a user's campaigns, including
// scraped (non-marketplace) ones where no buybox rank exists -- a guarantee-
// buybox rule would step such a product's price down every cycle with no
// rank to react to. So this form is undercut-only: no pricing-mode picker,
// no buybox fields. PricingModeGuide is rendered fixed in its undercut form
// (mode=false) to match the section layout & guide used by the other
// dynamic-pricing screens (ProductReprice, PricingOnboardingStep).
const TemplateForm = (props) => {

    const {value, onChange, t} = props;

    const template = value || {};

    const reductionTypes = [{name: t('reprice.amount'), value: 'amount'}, {name: t('reprice.ratio'), value: 'ratio'}];
    const reductionDirections = [{name: t('reprice.low'), value: 1}, {name: t('reprice.high'), value: -1}];
    const competitors = [{name: t('reprice.all'), value: false}, {name: t('reprice.only_specified'), value: true}];
    const statuses = [{name: t('actions.passive'), value: false}, {name: t('actions.active'), value: true}];
    const updateTypes = [{name: t('reprice.manuel_update'), value: false}, {name: t('reprice.auto_update'), value: true}];
    const noCompetitorTypes = [{name: t('yes_no.no'), value: false}, {name: t('yes_no.yes'), value: true}];

    const handleChange = (e) => {
        const {key, value: fieldValue} = getKeyValue(e);
        onChange({...template, [key]: fieldValue});
    };

    return <div className="p-grid">

        <div className="p-col-4">
            <label htmlFor="name">{t('pricing_template.name')}</label>
        </div>
        <div className="p-col-8">
            <InputText id="name" name="name" value={template.name || ""} onChange={handleChange}/>
        </div>

        <div className="p-col-12">
            <PricingModeGuide t={t} mode={false}/>
        </div>

        <SectionHeader label={t('reprice.section_strategy')} first/>
        <div className="p-col-4">
            <label>{t('reprice.reduction_type')}</label>
        </div>
        <div className="p-col-8">
            <SelectButton name="reduction_type" value={template.reduction_type} options={reductionTypes} optionLabel="name" onChange={handleChange}/>
        </div>

        <div className="p-col-4">
            <label>{t('reprice.reduction_direction')}</label>
        </div>
        <div className="p-col-8">
            <SelectButton name="reduction_direction" value={template.reduction_direction} options={reductionDirections} optionLabel="name" onChange={handleChange}/>
        </div>

        <div className="p-col-4">
            <label htmlFor="reduction_value">{t('reprice.reduction_value')}</label>
        </div>
        <div className="p-col-8">
            <Number id="reduction_value" name="reduction_value" value={template.reduction_value} onChange={(e) => handleChange(e)}/>
        </div>

        <SectionHeader label={t('reprice.section_protection')}/>
        <div className="p-col-4">
            <label>{t('reprice.protection_strategy')}</label>
            <Tooltip tooltip={t('reprice.protection_strategy_tooltip')}/>
        </div>
        <div className="p-col-8">
            <Dropdown inputId="protection_strategy" name="protection_strategy" options={protectionStrategies(t)} value={template.protection_strategy} onChange={handleChange}/>
        </div>

        <div className="p-col-4">
            <label>{t('reprice.no_competitor_strategy')}</label>
            <Tooltip tooltip={t('reprice.no_competitor_strategy_tooltip')}/>
        </div>
        <div className="p-col-8">
            <SelectButton name="no_competitor_strategy" value={template.no_competitor_strategy} options={noCompetitorTypes} optionLabel="name" onChange={handleChange}/>
        </div>

        <SectionHeader label={t('reprice.section_status')}/>
        <div className="p-col-4">
            <label>{t('reprice.is_active')}</label>
        </div>
        <div className="p-col-8">
            <SelectButton name="is_active" value={template.is_active} options={statuses} optionLabel="name" onChange={handleChange}/>
        </div>

        <div className="p-col-4">
            <label>{t('reprice.is_auto_update')}</label>
            <Tooltip tooltip={t('reprice.send_price_to_site_tooltip')}/>
        </div>
        <div className="p-col-8">
            <SelectButton name="is_auto_update" value={template.is_auto_update} options={updateTypes} optionLabel="name" onChange={handleChange}/>
        </div>

        <div className="p-col-4">
            <label>{t('reprice.ignore_some_competitors')}</label>
            <Tooltip tooltip={t('reprice.ignore_some_competitors_tooltip')}/>
        </div>
        <div className="p-col-8">
            <SelectButton name="ignore_some_competitors" value={template.ignore_some_competitors} options={competitors} optionLabel="name" onChange={handleChange}/>
        </div>

    </div>;
};

// Sub-header inside the form, mirroring the section-divider style used by
// ProductReprice for its "Strategy" / "Status" headers.
const SectionHeader = ({label, first}) => (
    <div className="p-col-12">
        <div style={{fontWeight: 700, color: '#374151', marginTop: first ? 4 : 8, borderBottom: '1px solid #eef2f7', paddingBottom: 4}}>
            {label}
        </div>
    </div>
);

export default TemplateForm;
